import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";

import { Sparkline } from "@/components/data/AreaChart";
import type { Series } from "@/components/data/AreaChart";
import { HelpTip } from "@/components/ui/HelpTip";
import { cn } from "@/lib/cn";

const ACCENT = {
  brand: { text: "text-brand-600" },
  go: { text: "text-go-600" },
  alert: { text: "text-alert-600" },
  ochre: { text: "text-ochre-600" },
  iris: { text: "text-iris-500" },
} as const;

export interface FigureTileProps {
  label: string;
  /** The number itself. Pre-formatted by the caller so units stay consistent. */
  value: string;
  /** Unit shown after the value at a smaller size: "days", "h", "staff". */
  unit?: string;
  /** One line of context under the figure, e.g. "of 30 accrued this year". */
  caption?: ReactNode;
  /** What the figure is and how it is counted, behind a question mark. */
  help?: ReactNode;
  /**
   * "board" is the brand-tinted tile used where a figure is the point of the
   * screen. It was near-black once, which put a slab in the middle of a light
   * interface that nothing else answered to.
   * "panel" stands on the grey ground as a white panel of its own.
   * "plain" is a soft well, for a figure nested inside a panel that already
   * has a heading.
   */
  tone?: "board" | "panel" | "plain";
  /**
   * Colours the figure and its mark.
   *
   * Only pass one where the colour is telling the reader something they would
   * otherwise have to work out: alert for a figure that needs acting on, ochre
   * for one to keep an eye on, go for one that is comfortably fine. A figure
   * that is merely a number stays ink, so the coloured ones still count.
   */
  accent?: "brand" | "go" | "alert" | "ochre" | "iris";
  /** A mark in the top-right, in the accent colour. */
  icon?: ReactNode;
  /**
   * The same figure over recent history, drawn small under it.
   *
   * A number on its own says what is true; a number with its own shape behind
   * it says whether that is normal. Only pass one where the history is
   * genuinely the same measure as the figure — a sparkline of something else
   * beside a big number is a lie told in pictures.
   */
  trend?: {
    values: number[];
    colour?: Series["colour"];
    label: string;
    /** How a hovered value is written, e.g. `(v) => `${v} h``. */
    format?: (value: number) => string;
  };
  /**
   * A signed change against the period before, e.g. `+3` or `-1.2`.
   *
   * Coloured by direction only when the caller says which direction is good,
   * because "overtime up 12%" and "headcount up 12%" are not the same news.
   */
  delta?: { text: string; direction: "up" | "down" | "flat"; good?: boolean };
  /** Under the caption: a way into what the figure counts, such as who it is. */
  footer?: ReactNode;
  className?: string;
}

/**
 * A single headline figure.
 *
 * The employee home screen leads with these because the question an employee
 * opens this app to answer is always "how many days do I have left".
 */
export function FigureTile({
  label,
  value,
  unit,
  caption,
  help,
  tone = "panel",
  accent,
  icon,
  trend,
  delta,
  footer,
  className,
}: FigureTileProps) {
  const onBoard = tone === "board";
  const value_colour = onBoard ? "text-board-text" : accent ? ACCENT[accent].text : "text-fg";

  return (
    <div
      className={cn(
        "flex flex-col justify-between gap-2.5 p-4",
        onBoard
          ? "rounded-lg bg-board text-board-text"
          : tone === "panel"
            ? "rounded-lg bg-surface ring-1 ring-inset ring-line"
            : "rounded-md bg-surface-sunken",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className={cn("u-label", onBoard && "text-board-muted")}>{label}</p>
        <span className="flex shrink-0 items-center gap-1">
          {help ? (
            <HelpTip subject={label} className="-my-1">
              {help}
            </HelpTip>
          ) : null}
          {icon ? (
            <span
              className={cn(
                onBoard ? "text-board-muted" : accent ? ACCENT[accent].text : "text-fg-placeholder",
              )}
              aria-hidden="true"
            >
              {icon}
            </span>
          ) : null}
        </span>
      </div>
      <p className="flex items-baseline gap-1.5">
        <span
          className={cn("u-display text-3xl font-bold tabular-nums", value_colour)}
        >
          {value}
        </span>
        {unit ? (
          <span
            className={cn(
              "text-base font-medium",
              onBoard ? "text-board-muted" : "text-fg-subtle",
            )}
          >
            {unit}
          </span>
        ) : null}
      </p>
      {trend && trend.values.length > 1 ? (
        <Sparkline
          values={trend.values}
          colour={trend.colour ?? (onBoard ? "brand" : (accent as Series["colour"]) ?? "brand")}
          label={trend.label}
          format={trend.format}
          className="-mb-1"
        />
      ) : null}

      {caption || delta ? (
        <p
          className={cn(
            "flex items-center gap-1.5 text-xs",
            onBoard ? "text-board-muted" : "text-fg-subtle",
          )}
        >
          {delta && delta.direction !== "flat" ? (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 font-semibold",
                delta.good === undefined
                  ? undefined
                  : delta.good
                    ? "text-fg-fine"
                    : "text-fg-waiting",
              )}
            >
              {delta.direction === "up" ? (
                <TrendingUp className="size-3.5" aria-hidden="true" />
              ) : (
                <TrendingDown className="size-3.5" aria-hidden="true" />
              )}
              {delta.text}
            </span>
          ) : null}
          {caption}
        </p>
      ) : null}
      {footer}
    </div>
  );
}
